import { AIAssistantMessage } from '@euler/model/ai';
import * as Clipboard from 'expo-clipboard';
import {
  Copy,
  RefreshCw,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react-native';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import Toast from 'react-native-root-toast';
import { useTheme } from 'styled-components/native';
import { StreamingShimmer } from '../../message/streaming/StreamingShimmer';
import { useAIAssistantActions } from '../AIAssistantProvider';
import { AIContentRenderer } from '../AIContentRenderer';
import { useStyles } from './styles';

function formatTime(date?: Date): string {
  if (!date) return '';
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

const AssistantBubbleContent = memo(function AssistantBubbleContent({
  displayContent,
  isStreaming,
  toolCall,
  onSuggestionPress,
  messageId,
  autoExecute,
}: {
  displayContent: string;
  isStreaming?: boolean;
  toolCall?: string;
  onSuggestionPress?: (text: string) => void;
  messageId?: string;
  autoExecute?: boolean;
}) {
  const hasContent = displayContent.length > 0;

  if (!hasContent && !isStreaming) return null;

  const showShimmer = isStreaming && (!hasContent || toolCall != null);

  return (
    <>
      {hasContent && (
        <AIContentRenderer
          content={displayContent}
          onSuggestionPress={onSuggestionPress}
          messageId={messageId}
          autoExecute={autoExecute}
          streaming={isStreaming}
        />
      )}
      {showShimmer && (
        <StreamingShimmer>{toolCall ?? '正在思考'}</StreamingShimmer>
      )}
    </>
  );
});

export const MessageBubble = memo(
  function MessageBubble({
    message,
    onSuggestionPress,
    onRetry,
    isError,
    toolCall,
  }: {
    message: AIAssistantMessage;
    onSuggestionPress?: (text: string) => void;
    onRetry?: () => void;
    isError?: boolean;
    toolCall?: string;
  }) {
    const theme = useTheme();
    const actions = useAIAssistantActions();
    const styles = useStyles();
    const isUser = message.role === 'user';
    const accentColor = theme.link;

    const displayContent = message.content;

    // autoExecute = the message went through `isStreaming=true → false`
    // in this React tree's lifetime (i.e. live-streamed this session).
    // A history-loaded message mounts with isStreaming=false and never
    // sees the transition, so its widgets won't auto-fire.
    const wasStreamingRef = useRef(false);
    useEffect(() => {
      if (message.isStreaming) wasStreamingRef.current = true;
    }, [message.isStreaming]);
    const autoExecute = wasStreamingRef.current && !message.isStreaming;

    const showReactions =
      !isUser && !message.isStreaming && displayContent.length > 0;

    const handleReaction = useCallback(
      (reaction: 'up' | 'down') => {
        actions.toggleReaction(message.id ?? '', message.localId, reaction);
      },
      [actions, message.id, message.localId],
    );

    const handleCopy = useCallback(() => {
      void Clipboard.setStringAsync(message.content);
      Toast.show('已复制', {
        duration: 500,
        position: Toast.positions.CENTER,
      });
    }, [message.content]);

    return (
      <View
        style={[
          styles.bubbleRow,
          isUser ? styles.bubbleRowUser : styles.bubbleRowAssistant,
        ]}
      >
        {!isUser && (
          <View
            style={[styles.avatar, { backgroundColor: accentColor + '12' }]}
          >
            <Sparkles color={accentColor} size={14} strokeWidth={2} />
          </View>
        )}
        <View
          style={isUser ? styles.bubbleUserOuter : styles.bubbleAssistantOuter}
        >
          <View
            style={[
              styles.bubble,
              isUser
                ? [styles.bubbleUser, { backgroundColor: accentColor }]
                : styles.bubbleAssistant,
              isError && styles.bubbleError,
              !isUser &&
                message.isStreaming &&
                displayContent.length === 0 && { alignSelf: 'flex-start' },
            ]}
          >
            {isUser ? (
              <Text style={styles.bubbleTextUser}>{message.content}</Text>
            ) : (
              <AssistantBubbleContent
                displayContent={displayContent}
                isStreaming={message.isStreaming}
                toolCall={toolCall}
                onSuggestionPress={onSuggestionPress}
                messageId={message.id ?? message.localId}
                autoExecute={autoExecute}
              />
            )}
          </View>
          <View
            style={[
              styles.bubbleMeta,
              isUser ? styles.bubbleMetaUser : styles.bubbleMetaAssistant,
            ]}
          >
            {message.createdAt && (
              <Text style={styles.timestamp}>
                {formatTime(message.createdAt)}
              </Text>
            )}
            {showReactions && (
              <View style={styles.reactionRow}>
                <TouchableOpacity
                  activeOpacity={0.6}
                  onPress={handleCopy}
                  hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
                  style={styles.reactionBtn}
                >
                  <Copy color="#94A3B8" size={12} strokeWidth={2} />
                </TouchableOpacity>
                <TouchableOpacity
                  activeOpacity={0.6}
                  onPress={() => handleReaction('up')}
                  hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
                  style={[
                    styles.reactionBtn,
                    message.reaction === 'up' && styles.reactionBtnActive,
                  ]}
                >
                  <ThumbsUp
                    color={message.reaction === 'up' ? accentColor : '#94A3B8'}
                    size={12}
                    strokeWidth={2}
                    fill={message.reaction === 'up' ? accentColor : 'none'}
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  activeOpacity={0.6}
                  onPress={() => handleReaction('down')}
                  hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
                  style={[
                    styles.reactionBtn,
                    message.reaction === 'down' && styles.reactionBtnActive,
                  ]}
                >
                  <ThumbsDown
                    color={message.reaction === 'down' ? '#EF4444' : '#94A3B8'}
                    size={12}
                    strokeWidth={2}
                    fill={message.reaction === 'down' ? '#EF4444' : 'none'}
                  />
                </TouchableOpacity>
              </View>
            )}
            {isError && onRetry && (
              <TouchableOpacity
                activeOpacity={0.7}
                onPress={onRetry}
                style={styles.retryButton}
              >
                <RefreshCw color="#EF4444" size={11} strokeWidth={2.5} />
                <Text style={styles.retryText}>重试</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    );
  },
  (prev, next) =>
    prev.message.content === next.message.content &&
    prev.message.isStreaming === next.message.isStreaming &&
    prev.message.reaction === next.message.reaction &&
    prev.isError === next.isError &&
    prev.toolCall === next.toolCall,
);
